import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { listProgressionEntries } from '../lib/last-played';
/**
 * M3WhatChanged — modal one-shot "ce qui change" (DEC-019 Q4).
 *
 * Trigger conditions :
 *   - localStorage flag `m3_taxonomy_onboarding_shown` NOT set
 *   - User a au moins 1 progression entry (= utilisateur M2 existant)
 *   - New users (no progression) skip ce screen (déjà voient l'onboarding
 *     standard avec nouvelle taxonomy)
 *
 * Visuel cyanotype sober : aucun em-dash, aucun "Welcome!", voix Vesperin.
 * Mapping M2→M3 explicit pour 6 anciens cats déplacés.
 */
const STORAGE_FLAG = 'labrain.m3_taxonomy_onboarding_shown';
function safeStorage() {
    if (typeof window === 'undefined')
        return null;
    try {
        return window.localStorage;
    }
    catch {
        return null;
    }
}
export function useM3OnboardingShown() {
    const [shouldShow, setShouldShow] = useState(false);
    useEffect(() => {
        const ls = safeStorage();
        if (!ls)
            return;
        const flag = ls.getItem(STORAGE_FLAG);
        if (flag === '1')
            return;
        // Trigger uniquement si user existant (≥1 entry de progression M2)
        const hasProgression = listProgressionEntries().length > 0;
        if (hasProgression)
            setShouldShow(true);
    }, []);
    const markShown = useCallback(() => {
        const ls = safeStorage();
        if (ls) {
            try {
                ls.setItem(STORAGE_FLAG, '1');
            }
            catch {
                /* ignore quota errors */
            }
        }
        setShouldShow(false);
    }, []);
    return { shouldShow, markShown };
}
export function M3WhatChanged() {
    const { t, i18n } = useTranslation(['onboarding']);
    const { shouldShow, markShown } = useM3OnboardingShown();
    if (!shouldShow)
        return null;
    // Mapping affiché côté user (FR ou EN selon locale active)
    const isFr = i18n.language?.startsWith('fr') ?? true;
    const map = isFr
        ? [
            { from: 'Logique', to: 'Cognition exécutive' },
            { from: 'Navigation', to: 'Mémoire et apprentissage' },
            { from: 'Émotion', to: 'Cognition sociale' },
            { from: 'Métacognition', to: 'Cognition exécutive' },
            { from: 'Créativité', to: 'Cognition exécutive' },
            { from: 'Décision', to: 'Cognition exécutive' },
        ]
        : [
            { from: 'Logic', to: 'Executive Functioning' },
            { from: 'Navigation', to: 'Memory and Learning' },
            { from: 'Emotion', to: 'Social Cognition' },
            { from: 'Metacognition', to: 'Executive Functioning' },
            { from: 'Creativity', to: 'Executive Functioning' },
            { from: 'Decision', to: 'Executive Functioning' },
        ];
    return (_jsx("div", { role: "dialog", "aria-modal": "true", "aria-labelledby": "m3-changed-title", className: "\n        fixed inset-0 z-[60] flex items-end justify-center\n        bg-cyan-text/40 backdrop-blur-sm\n        sm:items-center\n      ", onClick: markShown, children: _jsxs("div", { onClick: (e) => e.stopPropagation(), className: "\n          relative w-full max-w-[430px]\n          max-h-[88dvh] overflow-y-auto\n          border border-cyan-border bg-cyan-surface\n          p-6\n          flex flex-col gap-5\n        ", children: [_jsx("button", { type: "button", onClick: markShown, "aria-label": t('onboarding:m3_what_changed.close', { defaultValue: 'Fermer' }), className: "\n            absolute right-4 top-4\n            flex h-8 w-8 items-center justify-center\n            border border-cyan-border-faint\n            text-cyan-text-secondary\n            hover:border-cyan-network hover:text-cyan-text\n            transition\n          ", children: _jsx(X, { size: 14, strokeWidth: 1.6, "aria-hidden": true }) }), _jsxs("header", { className: "flex flex-col gap-2 pr-12", children: [_jsx("p", { className: "font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-network", children: t('onboarding:m3_what_changed.eyebrow', { defaultValue: 'Mise à jour M3' }) }), _jsx("h2", { id: "m3-changed-title", className: "font-display text-[24px] font-medium leading-[1.15] tracking-cy-display text-cyan-text", children: t('onboarding:m3_what_changed.title') })] }), _jsx("p", { className: "text-[13px] font-light leading-relaxed text-cyan-text-secondary", children: t('onboarding:m3_what_changed.body') }), _jsxs("div", { className: "border border-cyan-border-faint", children: [_jsx("p", { className: "border-b border-cyan-border-faint bg-cyan-bg px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-text-faint", children: t('onboarding:m3_what_changed.mapping_eyebrow', {
                                defaultValue: isFr ? 'Réorganisation' : 'Mapping',
                            }) }), _jsx("ul", { className: "divide-y divide-cyan-border-faint", children: map.map((m, i) => (_jsxs("li", { className: "flex items-center justify-between gap-3 px-4 py-3", children: [_jsx("span", { className: "text-[12px] text-cyan-text-faint line-through", children: m.from }), _jsx("span", { className: "font-mono text-[10px] text-cyan-network", children: "\u2192" }), _jsx("span", { className: "text-[13px] font-medium text-cyan-text text-right", children: m.to })] }, i))) })] }), _jsx("p", { className: "text-[11px] font-light text-cyan-text-faint", children: t('onboarding:m3_what_changed.note', {
                        defaultValue: isFr
                            ? 'Ta progression est conservée. Seuls les domaines sont réorganisés.'
                            : 'Your progress is preserved. Only domain labels were reorganized.',
                    }) }), _jsx("button", { type: "button", onClick: markShown, className: "\n            mt-2 w-full\n            border border-cyan-text bg-cyan-text text-cyan-surface\n            py-3 px-5\n            font-medium text-[14px]\n            transition active:scale-[0.99]\n            hover:bg-cyan-text-secondary\n          ", children: t('onboarding:m3_what_changed.cta') })] }) }));
}
//# sourceMappingURL=M3WhatChanged.js.map